import { useCountryContext } from '@archipro-website/localisation';
import heroNz from '../images/hero/hero-nz.webp';
import heroAu from '../images/hero/hero-au.jpg';
import heroUs from '../images/hero/hero-us.jpg';
import heroNzMobile from '../images/hero/hero-nz-mobile.webp';
import heroAuMobile from '../images/hero/hero-au-mobile.webp';
import heroUsMobile from '../images/hero/hero-us-mobile.webp';
import heroNzVideoWebm from '../images/hero/hero-nz.webm';
import heroNzVideoMp4 from '../images/hero/hero-nz.mp4';
import heroNzVideoPoster from '../images/hero/hero-nz-video-poster.webp';

/**
 * Per-market homepage hero (BC-4479).
 *
 * The hero is a project banner that differs by market, credited to the
 * featured project. A market with a `video` plays it on desktop, starting
 * from its own poster frame, while `image` stays the phone's banner; swap a market's
 * banner by changing its entry here. Both the logged-out hero (`HeroSection`) and the logged-in
 * hero (`AiHeroSection`) use the same banner image; only the logged-out hero
 * shows the credit overlay. `href` is a project-detail path resolved on the
 * visitor's own market domain, so a relative path is correct for every market.
 */
export interface HeroMarket {
    /** Full-bleed hero banner photo for the market (desktop / large screens). */
    image: string;
    /** Smaller portrait-cropped variant for mobile (≈9:16, ~828px wide). */
    mobileImage: string;
    /**
     * Muted looping banner video for desktop, in place of `image`. WebM for
     * browsers that play it, MP4 (H.264) for the rest, notably older Safari.
     * `poster` is the video's own first frame, so the banner does not change
     * picture when playback starts.
     */
    video?: { webm: string; mp4: string; poster: string };
    featuredProject: {
        name: string;
        architect: string;
        /** Project-detail path (relative — same domain as the visitor). */
        href: string;
    };
}

const NZ: HeroMarket = {
    image: heroNz,
    mobileImage: heroNzMobile,
    video: {
        webm: heroNzVideoWebm,
        mp4: heroNzVideoMp4,
        poster: heroNzVideoPoster,
    },
    featuredProject: {
        name: 'Whaitere',
        architect: 'Ponting Fitzgerald Architects',
        href: '/project/whaitere-ponting-fitzgerald-architects',
    },
};

const AU: HeroMarket = {
    image: heroAu,
    mobileImage: heroAuMobile,
    featuredProject: {
        name: 'Cape Schanck',
        architect: 'Studio Goss',
        href: '/project/cape-schnack-studio-goss',
    },
};

const US: HeroMarket = {
    image: heroUs,
    mobileImage: heroUsMobile,
    featuredProject: {
        name: 'Northshore Residence',
        architect: 'Lucid Architecture',
        href: '/project/northshore-residence-lucid-architecture',
    },
};

/** Resolves the hero banner + featured project for the visitor's market. */
export const useHeroMarket = (): HeroMarket =>
    useCountryContext().byCountry({ NZ, AU, US });
